<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Intake form editor -->
<template>
  <av-modal class="intake-form-editor" :visible="visible" @close="reset" :canCloseSelf="false" :showClose="false">

    <transition name="fade">
      <div class="editing-screen" v-if="editingField || addingNewField"></div>
    </transition>

    <!-- Editor -->
    <div class="modal-content editor">
      <div class="description-edit">
        <label class="av-label">Form Instructions</label>
        <textarea class="av-input" v-model="workingDescription"></textarea>
      </div>
      <div class="field-list" v-if="!previewing">
        <!-- Listing -->
        <div
          v-for="field of sortedWorkingForm" :key="field.id"
          class="field-listing"
          :class="{ inactive: addingNewField || (editingField && fieldToEdit.id !== field.id) }"
          @dragover="onDragOver($event, field)"
          @dragend="endDrag($event, field)"
          @dragstart="startDrag($event, field)"
          :field-id="field.id"
        >
            <div class="handle">
              <i class="fal fa-bars" @mousedown="allowDrag"></i>
            </div>
            <div class="listing-content">
              <div class="listing-header">
                <div class="label">{{ field.label }}</div>
                <span class="type"><i :class="[ typeIcon(field.type) ]"></i>{{ field.type }}</span>
              </div>
              <div class="listing-info">
                <div class="is-required">{{ field.required ? 'Required' : 'Optional' }}</div>
                <div class="actions">
                  <a class="av-link dark-gray" @click="beginEditingField(field)"><i class="fal fa-edit"></i> Edit</a>
                  <a class="av-link error" @click="remove(field.id)"><i class="fal fa-times-circle"></i> Remove</a>
                </div>
                </div>
            </div>

          <!-- Listing Editor -->
          <transition name="field-form">
            <div v-if="editingField && fieldToEdit.id === field.id" class="edit-field-form">
              <input type="text" class="av-input" v-model="fieldToEdit.label" placeholder="Field Label" />
              <input type="text" class="av-input" v-model="fieldToEdit.description" placeholder="Description" />
              <input v-if="fieldToEdit.type === 'radio'" type="text" class="av-input" v-model="fieldToEdit.options" placeholder="Options (ex. one, two)" />
              <av-checkbox v-model="fieldToEdit.required">Required</av-checkbox>

              <div class="checkbox-emails" v-if="fieldToEdit.type === 'checkbox'">
                <label class="av-label">Staff to Email:</label>
                <ul>
                  <li v-for="id of fieldToEdit.usersToEmail" :key="id">
                    <span>{{ getUserFromId(id).firstName }} {{ getUserFromId(id).lastName }}</span>
                    <span><i class="fal fa-times" @click="removeUserFromEmail(fieldToEdit.usersToEmail, id)"></i></span>
                  </li>
                  <li v-if="fieldToEdit.usersToEmail.length === 0">No Users Selected</li>
                </ul>
                <br>
                <av-select
                  :items="staff"
                  displayKey="firstName"
                  valueKey="id"
                  placeholder="Select new user to email"
                  :modelValue="tempUser"
                  @update:modelValue="addEmailToCheckboxField(fieldToEdit, $event)"
                ></av-select>
              </div>
              <br>
              <av-button class="hollow error small" @click="editingField = false">Cancel</av-button>
              <av-button class="small" @click="editField">Save</av-button>
            </div>
          </transition>
        </div>

        <!-- New Field Form -->
        <transition name="field-form">
          <div v-if="addingNewField" class="new-field-form">
            <av-select :items="fieldTypes" v-model="newFieldForm.type" />
            <input type="text" class="av-input" v-model="newFieldForm.label" placeholder="Field Label" />
            <input type="text" class="av-input" v-model="newFieldForm.description" placeholder="Description" />
            <input v-if="newFieldForm.type === 'radio' || newFieldForm.type === 'checkbox group'" type="text" class="av-input" v-model="newFieldForm.options" placeholder="Options (ex. one, two)" />
            <av-checkbox v-model="newFieldForm.required">Required</av-checkbox>

            <div class="checkbox-emails" v-if="newFieldForm.type === 'checkbox'">
              <label class="av-label">Staff to Email:</label>
              <ul>
                <li v-for="id of newFieldForm.usersToEmail" :key="id">
                  <span>{{ getUserFromId(id).firstName }} {{ getUserFromId(id).lastName }}</span>
                  <span><i class="fal fa-times" @click="removeUserFromEmail(newFieldForm.usersToEmail, id)"></i></span>
                </li>
                <li v-if="newFieldForm.usersToEmail.length === 0">No Users Selected</li>
              </ul>
              <br>
              <av-select
                :items="staff"
                displayKey="firstName"
                valueKey="id"
                placeholder="Select new user to email"
                :modelValue="tempUser"
                @update:modelValue="addEmailToCheckboxField(newFieldForm, $event)"
              ></av-select>
            </div>
            <br>
            <av-button class="hollow error small" @click="addingNewField = false">Cancel</av-button>
            <av-button class="small" @click.once="addField" :disable="!isActive">Add</av-button>
          </div>
        </transition>
      </div>

      <div class="new-button" v-if="!previewing">
        <a
          class="shaded green new-field-button"
          :class="{ inactive : editingField }"
          @click="beginAddingField"
        ><i class="fal fa-plus-circle"></i> Add New Field</a>
      </div>
    </div>

    <!-- Preview -->
    <div class="modal-content preview" v-if="previewing">
      <dynamic-field
        v-for="field of sortedWorkingForm"
        :key="field.id" :inputDefinition="field"
        v-model="previewForm[field.label]"
      ></dynamic-field>
    </div>

    <div class="modal-actions" :class="{ inactive: addingNewField || editingField }">
      <template v-if="!previewing">
        <av-button class="hollow error" @click="reset()">Cancel</av-button>
        <av-button class="hollow dark-gray" @click="showPreview">Preview</av-button>
        <av-button @click="save">Save</av-button>
      </template>
      <av-button v-if="previewing" class="hollow dark-gray" @click="previewing = false">Back</av-button>
    </div>
  </av-modal>
</template>

<script lang="ts">
import { cloneDeep } from 'lodash'
import { defineComponent, PropType } from 'vue'
import appointmentTypeService from '../common/appointmentTypeService'
import { DynamicInput } from '../models/dynamicInput'
import DynamicField from '../components/DynamicField.vue'
import IntakeForm from '../models/intakeForm'
import UserService from '../common/userService'
import User from '../models/user'

export default defineComponent({
  name: 'IntakeFormEditor',

  props: {
    /** The id of the AppointmentType the form belongs to */
    appointmentTypeId: {
      type: Number,
      required: true
    },

    /** The inputs of the intake form */
    intakeForm: {
      type: Object as PropType<IntakeForm>,
      required: true
    },

    /** Indicator to show or hide the editor */
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  emits: ['close', 'form-updated'],

  data () {
    return {
      isActive: true,

      /** Indicator to show form to add new field */
      addingNewField: false,

      /** Indicates that a field is being dragged */
      dragging: false,

      /** Indicator to show field editor */
      editingField: false,

      /** The field to edit */
      fieldToEdit: null as unknown as DynamicInput,

      /** Types of input */
      fieldTypes: ['text', 'email', 'phone', 'radio', 'checkbox', 'checkbox group', 'textbox'],

      /** The new field to be added */
      newFieldForm: { label: '', type: '', options: '', description: '', required: true, emailSupervisorIfChecked: false, usersToEmail: [] as Array<number> },

      /** Indicator to show preview */
      previewing: false,

      /** Object to contain preview responses */
      previewForm: {},

      /** List of OSP Staff */
      staff: [] as Array<User>,

      tempUser: null as null | number,

      /** Copy of the description */
      workingDescription: '',

      /** The copy of the inputs to edit */
      workingForm: [] as Array<DynamicInput>
    }
  },

  watch: {
    // When the inputs change, update the copy to edit
    intakeForm: {
      handler (newForm: IntakeForm) {
        this.workingForm = cloneDeep(newForm.inputs)
        this.workingDescription = newForm.description
      },
      deep: true,
      immediate: true
    }
  },

  computed: {
    /** returns sorted working form */
    sortedWorkingForm (): Array<DynamicInput> {
      return cloneDeep(this.workingForm).sort((a, b) => {
        return a.order > b.order ? 1 : -1
      })
    }
  },

  mounted () {
    UserService.getOspStaff().then(res => {
      this.staff = res.data
    })
  },

  methods: {
    /**
     * remove user from email
     */
    removeUserFromEmail (list: Array<number>, id: number) {
      const ind = list.findIndex(x => x === id)
      list.splice(ind, 1)
    },

    /**
     * Adds a new field to the list of inputs
     */
    addField () {
      this.isActive = false
      const newField = {
        id: 'a' + Math.random().toString().substring(2, 7),
        label: this.newFieldForm.label,
        type: this.newFieldForm.type,
        required: this.newFieldForm.required,
        description: this.newFieldForm.description,
        order: this.workingForm.length,
        options: [],
        emailSupervisorIfChecked: this.newFieldForm.emailSupervisorIfChecked,
        usersToEmail: this.newFieldForm.usersToEmail
      } as DynamicInput
      if (this.newFieldForm.type === 'radio' || this.newFieldForm.type === 'checkbox group') {
        newField.options = this.newFieldForm.options.split(/\s*(,|;)\s*/).filter(x => x !== ',')
      }

      this.workingForm.push(newField)
      this.addingNewField = false
      this.isActive = true
    },

    /**
     * add email to checkbox
     */
    addEmailToCheckboxField (field, id: number) {
      field.usersToEmail.push(id)
      this.tempUser = id
      setTimeout(() => {
        this.tempUser = null
      }, 0)
    },

    /**
     * Allows a field to be dragged
     */
    allowDrag (event: MouseEvent) {
      const target = event.target as HTMLElement
      const fieldEl = target.parentElement?.parentElement
      if (fieldEl) {
        fieldEl.setAttribute('draggable', 'true')
      }
    },

    /**
     * Resets the data for a new input and displays the form to define a new input
     */
    beginAddingField () {
      this.newFieldForm = {
        label: '',
        type: 'text',
        options: '',
        description: '',
        required: true,
        emailSupervisorIfChecked: false,
        usersToEmail: []
      }

      this.addingNewField = true

      const modalContent = (this.$el as HTMLElement).querySelector('.field-list')
      setTimeout(() => {
        if (modalContent) {
          modalContent.scrollTop = modalContent.scrollHeight
        }
      }, 0)
    },

    /**
     * Resets the edited field and shows the editor
     */
    beginEditingField (field: DynamicInput) {
      this.fieldToEdit = cloneDeep(field)
      this.editingField = true
    },

    /**
     * Saves the edit of a field
     */
    editField () {
      const currentField = this.workingForm.find(i => i.id === this.fieldToEdit.id)
      this.isActive = false
      if (currentField) {
        Object.assign(currentField, this.fieldToEdit)
      }
      this.editingField = false
      this.isActive = true
    },

    /**
     * Handles the end of a field being dragged
     */
    endDrag (event: MouseEvent, field: DynamicInput) {
      const target = event.target as HTMLElement
      target.classList.remove('dragging')

      // Perform reorder here somehow
      this.reorder()

      this.dragging = false
    },

    /**
     * Get staff from list given an id
     * @param id: The id of the staff
     */
    getUserFromId (id: number): User {
      return this.staff.find(u => u.id === id) as User
    },

    /**
     * Handles a field being dragged over
     */
    onDragOver (event: MouseEvent) {
      event.preventDefault()
      const container = document.querySelector('.field-list') as HTMLElement

      const draggableElements = [...document.querySelectorAll('.field-listing:not(.dragging)')]

      const afterElement = draggableElements.reduce((closest: any, child: any) => {
        const box = child.getBoundingClientRect()
        const offset = event.clientY - box.top - box.height / 2
        if (offset < 0 && offset > closest.offset) {
          return { offset: offset, element: child }
        } else {
          return closest
        }
      }, { offset: Number.NEGATIVE_INFINITY, element: null }).element

      const draggable = document.querySelector('.dragging') as HTMLElement

      if (afterElement == null) {
        container.appendChild(draggable)
      } else {
        container.insertBefore(draggable, afterElement)
      }
    },

    /**
     * Handes the start of a field being dragged
     */
    startDrag (event: MouseEvent) {
      const target = event.target as HTMLElement
      target.classList.add('dragging')
      this.dragging = true
    },

    /**
     * Returns an icon to represent the given input type
     * @param type: The input's type
     */
    typeIcon (type: string) {
      switch (type) {
        case 'text': return 'fal fa-text'
        case 'email': return 'fal fa-envelope'
        case 'phone': return 'fal fa-phone'
        case 'radio': return 'fal fa-circle'
        case 'checkbox': return 'fal fa-check-square'
        case 'checkbox group': return 'fal fa-check-double'
        case 'textbox': return 'fal fa-comment-alt'
      }
    },

    /**
     * Removes the given input
     * @param: The id of the input to remove
     */
    remove (id: string) {
      if (window.confirm('Are you sure?')) {
        const index = this.workingForm.findIndex(f => f.id === id)
        this.workingForm.splice(index, 1)
      }
    },

    /**
     * Sets the order of the fields based on current placement
     */
    reorder () {
      const listings = document.querySelectorAll('.field-listing')
      let place = 0
      listings.forEach(listingEl => {
        const fieldId = listingEl.getAttribute('field-id')
        const field = this.workingForm.find(f => f.id === fieldId)
        if (field) {
          field.order = place
        }
        place += 1
      })
    },

    /**
     * Resets the edited copy to be the current copy and emits event to close
     * the form
     */
    reset () {
      this.workingForm = cloneDeep(this.intakeForm.inputs)
      this.$emit('close')
    },

    /**
     * Makes a request to save the current copy and emits an event that the inputs
     * have been updated
     */
    save () {
      // Ensure correct order
      this.reorder()

      const formCopy = cloneDeep(this.intakeForm)
      formCopy.description = this.workingDescription
      formCopy.inputs = this.workingForm

      appointmentTypeService.updateIntakeForm(this.appointmentTypeId, formCopy).then(res => {
        this.$emit('form-updated', res.data)
      })
    },

    /**
     * Shows a preview of the current form
     */
    showPreview () {
      const previewForm = {}
      this.workingForm.forEach(w => {
        if (w.type === 'checkbox group') {
          previewForm[w.label] = []
        } else {
          previewForm[w.label] = ''
        }
      })
      this.previewForm = previewForm
      this.previewing = true
    }
  },

  components: {
    DynamicField
  }
})
</script>
